import { forwardRef } from 'react'

import { cn } from '@/lib/utils'

import { FxIcon } from './FxIcon'

/**
 * A Projekt on the overview strip: a `primary-tonal` folder on a `fill` field
 * over a white name bar with a kebab. 174px wide, `radius-sm`,
 * `shadow-raised`; selected adds the 2px `primary` outline.
 *
 * `href`, `onOpen` and `onMenu` are additions to the design system's
 * contract, for the same reason as on `FxTemplateCard`: the artboards make
 * the card a link, and the name carries it with a stretched pseudo-element so
 * the kebab stays a sibling rather than a button inside an anchor.
 */

export interface FxFolderCardProps
  extends Omit<React.HTMLAttributes<HTMLElement>, 'onSelect'> {
  name: string
  selected?: boolean
  href?: string
  onOpen?: () => void
  onMenu?: () => void
}

export const FxFolderCard = forwardRef<HTMLElement, FxFolderCardProps>(
  ({ name, selected, href, onOpen, onMenu, className, ...props }, ref) => {
    const text = 'min-w-0 flex-1 truncate text-left text-body-strong text-text'
    const stretch = 'after:absolute after:inset-0 after:content-[""]'
    return (
      <article
        ref={ref}
        className={cn(
          'relative box-border w-card-folder max-w-full overflow-hidden rounded-sm border-2 border-transparent bg-surface shadow-raised',
          selected && 'border-primary',
          className,
        )}
        {...props}
      >
        <div className="flex h-36 items-center justify-center overflow-hidden bg-fill text-primary-tonal">
          <FxIcon name="folder" size={150} filled />
        </div>
        <div className="flex h-[52px] items-center pl-4 pr-1.5">
          {href ? (
            <a href={href} className={cn(text, stretch, 'no-underline')} onClick={onOpen}>
              {name}
            </a>
          ) : onOpen ? (
            <button type="button" className={cn(text, stretch)} onClick={onOpen}>
              {name}
            </button>
          ) : (
            <span className={text}>{name}</span>
          )}
          {onMenu && (
            <button
              type="button"
              className="relative z-10 flex rounded-sm p-1 text-text"
              aria-label={`Aktionen für ${name}`}
              onClick={onMenu}
            >
              <FxIcon name="more_vert" size={22} />
            </button>
          )}
        </div>
      </article>
    )
  },
)
FxFolderCard.displayName = 'FxFolderCard'
